import {
  ProfileApprovalStatus,
  ProfileApprovalStatusText,
  getStatusStyle,
} from "@/constants/enums/approval-status.enum";
import {
  DocumentApprovalStatus,
  DocumentApprovalStatusText,
  getDocumentApprovalStatusStyle,
} from "@/constants/enums/document-approval-status.enum";
import {
  IMTSDocumentApprovalStatus,
  IMTSDocumentApprovalStatusText,
  getIMTSDocumentApprovalStatusStyle,
  IMTSDocumentStatus,
  IMTSDocumentStatusText,
  getIMTSDocumentStatusStyle,
} from "@/constants/enums/im-ts-document-status.enum";
import infoCircle from "@styles/all-images/icons/info.svg";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import { useState } from "react";
import ViewRejectionReason from "@/pages/SupplierOnBoarding/ViewRejectionReason";
import { Portal } from "@mui/material";

export enum StatusChipEnumType {
  ProfileApproval = "ProfileApproval",
  DocumentApproval = "DocumentApproval",
  IMTSDocumentApproval = "IMTSDocumentApproval",
  IMTSDocument = "IMTSDocument",
}

interface StatusChipsProps {
  status: number;
  type: StatusChipEnumType;
  showRejectionNote?: boolean;
  rejectionNote?: string | null;
  onRejectionNoteClick?: (note: string | null) => void;
}

const getStatusConfig = (
  status: number,
  type: StatusChipEnumType,
): {
  text: string;
  bgColor: string;
  textColor: string;
  isRejected: boolean;
  dotColor: string;
} => {
  switch (type) {
    case StatusChipEnumType.ProfileApproval:
      return {
        text:
          ProfileApprovalStatusText[status as ProfileApprovalStatus] ||
          NO_DATA_AVAILABLE,
        ...getStatusStyle(status as ProfileApprovalStatus),
        isRejected: status === ProfileApprovalStatus.R,
        dotColor: getStatusStyle(status as ProfileApprovalStatus).dotColor,
      };

    case StatusChipEnumType.DocumentApproval:
      return {
        text:
          DocumentApprovalStatusText[status as DocumentApprovalStatus] ||
          NO_DATA_AVAILABLE,
        ...getDocumentApprovalStatusStyle(status as DocumentApprovalStatus),
        isRejected: status === DocumentApprovalStatus.rejected,
        dotColor: getDocumentApprovalStatusStyle(
          status as DocumentApprovalStatus,
        ).dotColor,
      };

    case StatusChipEnumType.IMTSDocumentApproval:
      return {
        text:
          IMTSDocumentApprovalStatusText[
            status as IMTSDocumentApprovalStatus
          ] || NO_DATA_AVAILABLE,
        ...getIMTSDocumentApprovalStatusStyle(
          status as IMTSDocumentApprovalStatus,
        ),
        isRejected: status === IMTSDocumentApprovalStatus.Rejected,
        dotColor: getIMTSDocumentApprovalStatusStyle(
          status as IMTSDocumentApprovalStatus,
        ).dotColor,
      };

    case StatusChipEnumType.IMTSDocument:
      return {
        text:
          IMTSDocumentStatusText[status as IMTSDocumentStatus] ||
          NO_DATA_AVAILABLE,
        ...getIMTSDocumentStatusStyle(status as IMTSDocumentStatus),
        isRejected: false,
        dotColor: getIMTSDocumentStatusStyle(status as IMTSDocumentStatus)
          .dotColor,
      };

    default:
      return {
        text: NO_DATA_AVAILABLE,
        bgColor: "tw-bg-gray-100",
        textColor: "tw-text-gray-800",
        isRejected: false,
        dotColor: "tw-bg-[#667085]",
      };
  }
};

const StatusChips: React.FC<StatusChipsProps> = ({
  status,
  type,
  showRejectionNote = false,
  rejectionNote = null,
  onRejectionNoteClick,
}) => {
  const statusConfig = getStatusConfig(status, type);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);

  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <ul className="tw-list-none tw-p-0">
        <li
          className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${statusConfig.bgColor} ${statusConfig.textColor}`}
        >
          <span
            className={`tw-w-2 tw-h-2 tw-rounded-full ${statusConfig.dotColor}`}
          />
          <span>{statusConfig.text}</span>
        </li>
      </ul>

      {showRejectionNote && statusConfig.isRejected && (
        <button
          onClick={() => setIsRejectionModalOpen(true)}
          title="View Rejection Note"
        >
          <img src={infoCircle} alt="info" className="tw-w-4 tw-h-4" />
        </button>
      )}

      {isRejectionModalOpen && (
        <Portal>
          <ViewRejectionReason
            onClose={() => setIsRejectionModalOpen(false)}
            rejectionReason={rejectionNote || ""}
            onSubmit={() => {}}
          />
        </Portal>
      )}
    </div>
  );
};

export default StatusChips;
